import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  alpha,
  Stack,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import { useTranslation } from "react-i18next";

function resolveErrorKind(error) {
  const status = error?.status ?? error?.response?.status ?? null;
  const message = String(error?.message || "").toLowerCase();
  if (status === 503 || message.includes("schema") || message.includes("indisponível")) {
    return "schema";
  }
  if (status === 403) return "forbidden";
  if (status === 401) return "unauthorized";
  return "generic";
}

const TeamTasksLoadError = ({ error, onRetry, isRetrying = false }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const kind = resolveErrorKind(error);

  const titleKey =
    kind === "schema"
      ? "loadErrorSchemaTitle"
      : kind === "forbidden"
        ? "loadErrorForbiddenTitle"
        : kind === "unauthorized"
          ? "loadErrorUnauthorizedTitle"
          : "loadErrorTitle";

  const bodyKey =
    kind === "schema"
      ? "loadErrorSchemaBody"
      : kind === "forbidden"
        ? "loadErrorForbiddenBody"
        : kind === "unauthorized"
          ? "loadErrorUnauthorizedBody"
          : "loadErrorGenericBody";

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.35 : 0.25)}`,
        bgcolor: alpha(theme.palette.error.main, isDark ? 0.08 : 0.04),
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.error.main, isDark ? 0.15 : 0.1),
            }}
          >
            <CloudOffOutlinedIcon color="error" sx={{ fontSize: 32 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h2" fontWeight={700} gutterBottom>
              {t(titleKey)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
              {t(bodyKey)}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            disabled={isRetrying}
            sx={{ flexShrink: 0, fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            {isRetrying ? t("loading") : t("loadErrorRetry")}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default TeamTasksLoadError;
